import io from 'socket.io-client';
import { get, isEmpty, isNil } from 'lodash-es';
import * as Sentry from '@sentry/react';
import { serverUrl } from '../global/Environment';
import { SOCKET } from '../actions/types';
import { bulkEditModeSelector, taskListCurrentTabSelector, yearSelector } from '../selectors/taskListSelectors';
import { currentTaskSelector } from '../selectors/taskDetailSelectors';
import { TAGS } from '../api/baseApi';
import { opsSystemApi } from '../api/opsSystemApi';
import { TASK_LIST_TABS } from '../constants';

export const createSocketMiddleware = () => (store) => {
  let socket = null;

  return (next) => (action) => {
    switch (action.type) {
      case SOCKET.CONNECT:
        if (socket) {
          socket.disconnect();
        }
        socket = createSocket(store);
        return;
      case SOCKET.DISCONNECT:
        if (socket) {
          socket.disconnect();
        }
        socket = null;
        return;
      case SOCKET.SUBSCRIBE_TASK_LIST:
        if (socket) {
          socket.emit('join task list');
        }
        return;
      case SOCKET.SUBSCRIBE_TASK_DETAIL:
        if (socket) {
          socket.emit('join task room', action.payload);
        }
        return;
      default:
        return next(action);
    }
  };
};

export const createSocket = (store) => {
  const socket = io(`${serverUrl()}qdeck`, {
    transports: ['websocket'],
    auth: (cb) => {
      cb({
        token: localStorage.getItem('KeeperToken')
      });
    }
  });

  socket.io.on('open', () => {
    console.log(`${new Date()}: socket connected`);
  });

  socket.io.on('reconnect', async () => {
    const { data: taskList } = await store.dispatch(opsSystemApi.endpoints.getTasks.initiate);
    const currentTask = currentTaskSelector(store.getState());

    if (get(currentTask, 'id')) {
      store.dispatch(
        opsSystemApi.util.invalidateTags([
          TAGS.TASK,
          TAGS.TASK_COMMENTS,
          TAGS.TASK_ACTIVITIES,
          TAGS.TASK_FORMS,
          TAGS.ASK_LOG,
          TAGS.USER_ESCALATION_ERRORS
        ])
      );
      socket.emit('join task room', currentTask.id);
    } else if (taskList) {
      store.dispatch(opsSystemApi.util.invalidateTags([TAGS.TASK_LIST]));
      socket.emit('join task list');
    }
  });

  socket.io.on('error', (e) => {
    console.error('socket encountered error: ', e.message, 'Closing socket');
  });

  socket.io.on('close', (reason) => {
    console.log(`${new Date()}: socket disconnected -- ${reason}`);
  });

  socket.on('connect_error', (error) => {
    console.error('There was an error connecting the socket: ', error.message);
    Sentry.captureException(error);
  });

  socket.on('message', async (action) => {
    const payload = get(action, 'payload');
    const { data: taskList } = await store.dispatch(opsSystemApi.endpoints.getTasks.initiate);
    const bulkEditMode = bulkEditModeSelector(store.getState());

    switch (action.type) {
      case 'TASK_UPDATE': {
        const currentTask = currentTaskSelector(store.getState());
        const updatedTaskId = get(payload, 'taskId');
        const updatedTaskField = get(payload, 'field');
        const currentTab = taskListCurrentTabSelector(store.getState());

        if (isEmpty(currentTask)) {
          if (
            taskList &&
            isNil(bulkEditMode) &&
            (updatedTaskField === 'assignee' ||
              updatedTaskField === 'stage' ||
              (updatedTaskField === 'substages' && currentTab === TASK_LIST_TABS.SUBSTAGE_TASKS) ||
              updatedTaskField === 'comments')
          ) {
            store.dispatch(opsSystemApi.util.invalidateTags([TAGS.TASK_LIST]));
          }
        } else if (currentTask.id === updatedTaskId) {
          if (updatedTaskField === 'comments') {
            store.dispatch(opsSystemApi.util.invalidateTags([TAGS.TASK_COMMENTS]));
          } else {
            store.dispatch(opsSystemApi.util.invalidateTags([TAGS.TASK]));
          }
        }

        return;
      }
      case 'TASK_CREATE': {
        const taskListYear = yearSelector(store.getState());
        const newTaskYear = get(payload, 'year');
        if (taskList && isNil(bulkEditMode) && taskListYear === newTaskYear) {
          store.dispatch(opsSystemApi.util.invalidateTags([TAGS.TASK_LIST]));
        }
        return;
      }
      case 'GENERATE_TAX_RETURN': {
        const pdfUrl = get(payload, 'url');
        window.open(pdfUrl, '_blank');
        return;
      }
      default: {
        return;
      }
    }
  });

  return socket;
};
